import {
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { CellMeta, PlayerStats } from "@/lib/types";
import { cn } from "@/lib/utils";

import { createColumns } from "./PlayerColumns";

interface PlayerTableProps<TData extends RowData> {
  columns: ReturnType<typeof createColumns>;
  data: TData[];
}

export function PlayerTable({ columns, data }: PlayerTableProps<PlayerStats>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="shadow overflow-hidden border-b border-gray-200">
      <Table>
        <TableHeader className="bg-slate-500">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead className="text-white" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="text-gray-700">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                className={cn(
                  "transition-colors hover:bg-gray-200 data-[state=selected]:bg-muted",
                  {
                    "bg-gray-100": index % 2 !== 0,
                  },
                )}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      (cell.column.columnDef.meta as CellMeta)?.className,
                      {
                        "text-right":
                          (cell.column.columnDef.meta as CellMeta)?.align ===
                          "right",
                        "text-left":
                          (cell.column.columnDef.meta as CellMeta)?.align ===
                          "left",
                        "text-center":
                          (cell.column.columnDef.meta as CellMeta)?.align ===
                          "center",
                      },
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default PlayerTable;
